import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import CustomerSearchField from "@/components/CustomerSearchField";
import FinishedProductPicker from "@/components/FinishedProductPicker";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";
import { Factory, Plus } from "lucide-react";

const ORDER_STATUS = ["DRAFT", "PENDING", "ACCEPTED", "REJECTED", "FULFILLED"];
const PROD_STATUS = ["QUEUED", "IN_PRODUCTION", "READY", "DISPATCHED"];

const STATUS_COLORS = {
  QUEUED: "bg-blue-100 text-blue-900",
  IN_PRODUCTION: "bg-amber-100 text-amber-900",
  READY: "bg-emerald-100 text-emerald-900",
  DISPATCHED: "bg-slate-200 text-slate-900",
  OPEN: "bg-blue-100 text-blue-900",
  PENDING: "bg-amber-100 text-amber-900",
  ACCEPTED: "bg-emerald-100 text-emerald-900",
  REJECTED: "bg-red-100 text-red-900",
  DRAFT: "bg-secondary",
};

const BLANK = {
  customer_name: "", customer_email: "",
  salesperson_name: "",
  product_id: "", product_code: "", product_name: "",
  unit: "box", unit_price: 0,
  quantity: 1, target_date: "", notes: "",
};

export default function SalesOrdersQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = () => {
    setLoading(true);
    api.get("/manufacturing/sales-orders-queue")
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const patchOrder = async (r, field, value) => {
    try {
      await api.patch(`/manufacturing/sales-orders/${r.id}`, { [field]: value });
      toast.success(`${field === "status" ? "Order" : "Production"} status → ${value}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    if (!form.customer_name) { toast.error("Customer is required"); return; }
    if (!form.product_name) { toast.error("Pick a finished product"); return; }
    if (Number(form.quantity) <= 0) { toast.error("Quantity must be positive"); return; }
    try {
      await api.post("/manufacturing/sales-orders", {
        customer_name: form.customer_name,
        salesperson_name: form.salesperson_name || undefined,
        product_id: form.product_id || undefined,
        product_code: form.product_code || undefined,
        product_name: form.product_name,
        quantity: Number(form.quantity),
        unit: form.unit,
        unit_price: Number(form.unit_price || 0),
        target_date: form.target_date || undefined,
        notes: form.notes || undefined,
      });
      toast.success("Sales order created");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="prod-sales-queue-page">
      <BackButton />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-3"><Factory className="w-7 h-7" /> Sales orders queue</h1>
          <p className="text-muted-foreground">Orders raised by the sales team. Edit order and production status inline. Accepting an order with deficit stock auto-creates a production order.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(BLANK); }}>
          <DialogTrigger asChild><Button data-testid="new-sales-order-btn"><Plus className="w-4 h-4 mr-1" /> New sales order</Button></DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New sales order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <CustomerSearchField
                testid="so-customer-search"
                label="Customer"
                placeholder="Search customer…"
                value={form.customer_email ? { name: form.customer_name, email: form.customer_email } : null}
                onChange={(opt) => setForm({ ...form, customer_name: opt.name || opt.email || "", customer_email: opt.email || "" })}
              />
              <div><Label>Salesperson</Label><Input value={form.salesperson_name} onChange={(e) => setForm({ ...form, salesperson_name: e.target.value })} placeholder="Leave blank to use your name" data-testid="so-salesperson" /></div>
              <div className="space-y-1">
                <Label>Product</Label>
                <FinishedProductPicker
                  value={form.product_id}
                  onPick={(p) => setForm({ ...form, product_id: p.id, product_code: p.code, product_name: p.name, unit: p.unit || "box", unit_price: p.sale_price || 0 })}
                  testId="so-product-picker"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Qty</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="so-qty" /></div>
                <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                <div><Label>Unit price</Label><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="so-price" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Target date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <div className="border-t border-border pt-2 flex justify-between text-sm">
                <span className="text-muted-foreground">Estimated amount</span>
                <span className="font-mono font-bold">₹{(Number(form.quantity || 0) * Number(form.unit_price || 0)).toLocaleString()}</span>
              </div>
              <Button onClick={submit} className="w-full" data-testid="so-save-btn">Create sales order</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Salesperson</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Order status</TableHead>
              <TableHead>Production status</TableHead>
              <TableHead>Target date</TableHead>
              <TableHead>Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`sales-order-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.number}</TableCell>
                <TableCell className="text-sm">{r.customer_name}</TableCell>
                <TableCell className="text-sm">
                  {r.product_name || "—"}
                  {r.product_code ? <div className="text-[10px] text-muted-foreground font-mono">{r.product_code}</div> : null}
                </TableCell>
                <TableCell className="text-xs">{r.salesperson_name || "—"}</TableCell>
                <TableCell className="font-mono">{r.total_qty}</TableCell>
                <TableCell>
                  <Select value={r.status || "DRAFT"} onValueChange={(v) => patchOrder(r, "status", v)}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`sales-order-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ORDER_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={r.production_status || "QUEUED"} onValueChange={(v) => patchOrder(r, "production_status", v)}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`sales-order-prod-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PROD_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs">{r.target_date ? new Date(r.target_date).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="font-mono">₹{Number(r.amount || 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No sales orders waiting on production.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
